/**
 * Canonical Market Research stage identity and ordering.
 *
 * Backend stage codes are durable identifiers, not visual positions. Brand-name
 * J&A keeps backend code 14 while appearing after Product Discovery (code 3),
 * and DD/DAF 2579 keeps backend code 13 before the solicitation decision.
 */

export const MARKET_RESEARCH_STAGE_ORDER = [
  1, 2, 3, 14, 4, 5, 6, 7, 8, 9, 10, 13, 11, 12,
] as const;

export type MarketResearchStageCode = (typeof MARKET_RESEARCH_STAGE_ORDER)[number];
export type MarketResearchStageApplicabilityRule =
  | 'always'
  | 'brandNameJaRequired'
  | 'dd2579Required';

export interface MarketResearchStageApplicability {
  brandNameJaRequired: boolean;
  dd2579Required: boolean;
}

export interface MarketResearchStageDescriptor {
  id: string;
  name: string;
  description: string;
  backendCode: MarketResearchStageCode;
  /** Fixed position in the full canonical order, including conditional stages. */
  visualPosition: number;
  applicability: MarketResearchStageApplicabilityRule;
  /** How an inapplicable conditional stage is represented in the stepper. */
  whenNotApplicable?: 'disabled' | 'hidden';
  disabledReason?: string;
}

export interface VisibleMarketResearchStage extends MarketResearchStageDescriptor {
  applicable: boolean;
  disabled: boolean;
  disabledReason: string | undefined;
}

const STAGE_DETAILS: Record<MarketResearchStageCode, Omit<
  MarketResearchStageDescriptor,
  'backendCode' | 'visualPosition'
>> = {
  1: {
    id: 'questions',
    name: 'Generate Questions',
    description: 'Generate clarifying questions for the commodity requirements',
    applicability: 'always',
  },
  2: {
    id: 'mfr',
    name: 'Salient Characteristics',
    description: 'Generate Salient Characteristics based on answers',
    applicability: 'always',
  },
  3: {
    id: 'products',
    name: 'Product Discovery',
    description: 'Find products that match your requirements',
    applicability: 'always',
  },
  14: {
    id: 'brand-name-ja',
    name: 'Brand-name J&A',
    description: 'Document the justification for a restrictive brand-name acquisition',
    applicability: 'brandNameJaRequired',
    whenNotApplicable: 'disabled',
    disabledReason: 'Not required',
  },
  4: {
    id: 'psc',
    name: 'PSC Code Assignment',
    description: 'Assign Product Service Codes to selected products',
    applicability: 'always',
  },
  5: {
    id: 'naics',
    name: 'NAICS Code Mapping',
    description: 'Map PSC codes to NAICS industry classifications',
    applicability: 'always',
  },
  6: {
    id: 'vendors',
    name: 'Vendor Discovery',
    description: 'Find qualified vendors based on NAICS codes',
    applicability: 'always',
  },
  7: {
    id: 'section-b',
    name: 'Section B Generation',
    description: 'Generate Section B - Supplies or Services and Prices/Costs',
    applicability: 'always',
  },
  8: {
    id: 'section-c',
    name: 'Section C Generation',
    description: 'Generate Section C - Acquisition Requirements Documentation',
    applicability: 'always',
  },
  9: {
    id: 'section-d',
    name: 'Section D Generation',
    description: 'Generate Section D - Market Research Techniques Used',
    applicability: 'always',
  },
  10: {
    id: 'section-e',
    name: 'Section E Generation',
    description: 'Generate Section E - Available Sources/Vendors',
    applicability: 'always',
  },
  11: {
    id: 'synopsis',
    name: 'Synopsis & MRR',
    description: 'Generate Combined Synopsis/Solicitation (Stage 11)',
    applicability: 'always',
  },
  12: {
    id: 'vendor-bids',
    name: 'Vendor Bids',
    description: 'Upload vendor quotes and generate Abstract of Offers',
    applicability: 'always',
  },
  13: {
    id: 'dd2579',
    name: 'DD/DAF 2579',
    description: 'Document the required small-business coordination record',
    applicability: 'dd2579Required',
    whenNotApplicable: 'hidden',
  },
};

export const MARKET_RESEARCH_STAGE_DESCRIPTORS: readonly MarketResearchStageDescriptor[] =
  MARKET_RESEARCH_STAGE_ORDER.map((backendCode, index) => ({
    ...STAGE_DETAILS[backendCode],
    backendCode,
    visualPosition: index + 1,
  }));

/**
 * The Customer MR surface is intentionally a route descriptor of its own,
 * rather than an index-based filter of the contracting route.  The durable
 * Stage 14 code is visually next after Product Discovery only when the
 * server-authoritative Stage-3 decision requires a Brand-name J&A.
 *
 * This is a client-side presentation projection. The backend remains the
 * authority for capability access and validates the persisted decision.
 */
export function getCustomerMarketResearchStages(
  brandNameJaRequired: boolean,
): MarketResearchStageDescriptor[] {
  // Customer authoring ends at Section C (durable code 8). This remains a
  // separate projection from the canonical CO/CS ordering, where J&A's code
  // 14 is intentionally displayed between Product Discovery and PSC.
  const allowedCodes = brandNameJaRequired
    ? [1, 2, 3, 14, 4, 5, 6, 7, 8]
    : [1, 2, 3, 4, 5, 6, 7, 8];
  return allowedCodes.flatMap((backendCode) => {
    const stage = getMarketResearchStageByCode(backendCode);
    return stage ? [stage] : [];
  });
}

/** Resolve a durable backend code without relying on numeric/index arithmetic. */
export function getMarketResearchStageByCode(
  backendCode: number,
): MarketResearchStageDescriptor | undefined {
  return MARKET_RESEARCH_STAGE_DESCRIPTORS.find((stage) => stage.backendCode === backendCode);
}

export function isMarketResearchStageApplicable(
  stage: MarketResearchStageDescriptor,
  applicability: MarketResearchStageApplicability,
): boolean {
  return stage.applicability === 'always' || applicability[stage.applicability];
}

/** The ordered stages contributing to progress positions and the denominator. */
export function getApplicableMarketResearchStages(
  applicability: MarketResearchStageApplicability,
): MarketResearchStageDescriptor[] {
  return MARKET_RESEARCH_STAGE_DESCRIPTORS.filter((stage) =>
    isMarketResearchStageApplicable(stage, applicability));
}

/**
 * Ordered stepper rows. Inactive Stage 14 remains visible and disabled, while
 * inactive Stage 13 retains the existing conditional-hidden behavior.
 */
export function getVisibleMarketResearchStages(
  applicability: MarketResearchStageApplicability,
): VisibleMarketResearchStage[] {
  return MARKET_RESEARCH_STAGE_DESCRIPTORS.flatMap((stage) => {
    const applicable = isMarketResearchStageApplicable(stage, applicability);
    if (!applicable && stage.whenNotApplicable === 'hidden') return [];
    return [{
      ...stage,
      applicable,
      disabled: !applicable,
      disabledReason: applicable ? undefined : stage.disabledReason,
    }];
  });
}

export function getMarketResearchStageCount(
  applicability: MarketResearchStageApplicability,
): number {
  return getApplicableMarketResearchStages(applicability).length;
}

/** Return the 1-based applicable progress position, or null when inapplicable/unknown. */
export function getMarketResearchStagePosition(
  backendCode: number,
  applicability: MarketResearchStageApplicability,
): number | null {
  const index = getApplicableMarketResearchStages(applicability)
    .findIndex((stage) => stage.backendCode === backendCode);
  return index < 0 ? null : index + 1;
}

/** Resolve a 1-based applicable progress position to its stable backend code. */
export function getMarketResearchBackendCodeAtPosition(
  position: number,
  applicability: MarketResearchStageApplicability,
): MarketResearchStageCode | undefined {
  if (!Number.isInteger(position) || position < 1) return undefined;
  return getApplicableMarketResearchStages(applicability)[position - 1]?.backendCode;
}

/** Find the nearest enabled stage before a code in the full canonical order. */
export function getPreviousApplicableMarketResearchStage(
  backendCode: number,
  applicability: MarketResearchStageApplicability,
): MarketResearchStageDescriptor | undefined {
  const canonicalIndex = MARKET_RESEARCH_STAGE_DESCRIPTORS
    .findIndex((stage) => stage.backendCode === backendCode);
  if (canonicalIndex <= 0) return undefined;

  for (let index = canonicalIndex - 1; index >= 0; index -= 1) {
    const candidate = MARKET_RESEARCH_STAGE_DESCRIPTORS[index];
    if (isMarketResearchStageApplicable(candidate, applicability)) return candidate;
  }
  return undefined;
}

/** Coordination happens before solicitation; Vendor Bids remains the endpoint. */
export function getMarketResearchTerminalStageCode(
  _applicability: Pick<MarketResearchStageApplicability, 'dd2579Required'>,
): 12 {
  return 12;
}
